/** Original Hearthhop drawing vocabulary. All coordinates are world pixels. */
export const INK = '#293f39';
export const PAPER = '#fff6df';
export const AMBER = '#e9b34f';
export const TAU = Math.PI * 2;

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function mix(a: number, b: number, amount: number): number {
  return a + (b - a) * amount;
}

export function hash(value: string): number {
  let result = 2166136261;
  for (let i = 0; i < value.length; i++) result = Math.imul(result ^ value.charCodeAt(i), 16777619);
  return result >>> 0;
}

export function random(seed: number): () => number {
  let state = seed || 1;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 4294967296;
  };
}

export function rounded(
  ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r = 5,
): void {
  r = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export function disc(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fillStyle = fill;
  ctx.fill();
}

export function line(
  ctx: CanvasRenderingContext2D, points: number[], color: string, width = 2,
): void {
  ctx.beginPath();
  ctx.moveTo(points[0], points[1]);
  for (let i = 2; i < points.length; i += 2) ctx.lineTo(points[i], points[i + 1]);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

export function text(
  ctx: CanvasRenderingContext2D, label: string, x: number, y: number,
  size = 12, color = INK, align: CanvasTextAlign = 'center', weight = 700,
): void {
  ctx.font = `${weight} ${size}px "Trebuchet MS", "Avenir Next", system-ui, sans-serif`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  ctx.fillText(label, x, y);
}

export function pill(
  ctx: CanvasRenderingContext2D, label: string, x: number, y: number,
  options: { fill?: string; color?: string; stroke?: string; size?: number; padding?: number; height?: number } = {},
): number {
  const size = options.size ?? 11;
  ctx.font = `800 ${size}px "Trebuchet MS", "Avenir Next", system-ui, sans-serif`;
  const width = ctx.measureText(label).width + (options.padding ?? 18);
  const height = options.height ?? 23;
  rounded(ctx, x - width / 2, y - height / 2, width, height, height / 2);
  ctx.fillStyle = options.fill ?? PAPER;
  ctx.fill();
  if (options.stroke) {
    ctx.strokeStyle = options.stroke;
    ctx.lineWidth = 1.4;
    ctx.stroke();
  }
  text(ctx, label, x, y + 0.5, size, options.color ?? INK, 'center', 800);
  return width;
}

export function spark(
  ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string,
): void {
  ctx.beginPath();
  ctx.moveTo(x, y - radius);
  ctx.quadraticCurveTo(x + radius * 0.22, y - radius * 0.22, x + radius * 0.68, y);
  ctx.quadraticCurveTo(x + radius * 0.22, y + radius * 0.22, x, y + radius);
  ctx.quadraticCurveTo(x - radius * 0.22, y + radius * 0.22, x - radius * 0.68, y);
  ctx.quadraticCurveTo(x - radius * 0.22, y - radius * 0.22, x, y - radius);
  ctx.fillStyle = color;
  ctx.fill();
}

export function leaf(
  ctx: CanvasRenderingContext2D, x: number, y: number, length: number, angle: number, color: string,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(-length * 0.5, -length * 0.25, -length * 0.32, -length * 0.88, 0, -length);
  ctx.bezierCurveTo(length * 0.5, -length * 0.66, length * 0.35, -length * 0.12, 0, 0);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
}

/** An arch with a flat threshold. Its full shape stays within the supplied rectangle. */
export function arch(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const radius = Math.min(w / 2, h * 0.48);
  ctx.beginPath();
  ctx.moveTo(x, y + h);
  ctx.lineTo(x, y + radius);
  ctx.bezierCurveTo(x, y - radius * 0.33, x + w, y - radius * 0.33, x + w, y + radius);
  ctx.lineTo(x + w, y + h);
  ctx.closePath();
}

export function circuitMark(
  ctx: CanvasRenderingContext2D, x: number, y: number, index: number, active: boolean, radius = 9,
): void {
  // Letters and alternating silhouettes carry the meaning without relying on hue.
  ctx.save();
  if (index % 2) {
    rounded(ctx, x - radius, y - radius, radius * 2, radius * 2, 3);
  } else {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, TAU);
  }
  ctx.fillStyle = active ? AMBER : '#eee7d3';
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.6;
  ctx.stroke();
  text(ctx, String.fromCharCode(65 + index % 26), x, y + 0.4, radius + 1, INK, 'center', 900);
  ctx.restore();
}
